/**
 * SAAS-USD-BILLING-1 · Phase 1 — Store Profile card that shows the SaaS
 * checkout-currency configuration. Rendered only when
 * `merchant_type === "SAAS_DIGITAL_SERVICES"` — Retail merchants never see
 * this UI, and UNCLASSIFIED merchants don't either (they're on the legacy
 * CAD-only path until they classify).
 */
import { useState } from "react";
import { Coins, CircleDollarSign, DollarSign } from "lucide-react";
import SaasCheckoutCurrencyModal from "./SaasCheckoutCurrencyModal";

export default function SaasCheckoutCurrencyCard({ store, onUpdated }) {
  const [open, setOpen] = useState(false);

  const merchantType = store?.merchant_type || "UNCLASSIFIED";
  if (merchantType !== "SAAS_DIGITAL_SERVICES") return null;

  const cc = store.checkout_currency || {
    applicable: true,
    supported_checkout_currencies: store.supported_checkout_currencies || [],
    primary_checkout_currency: store.primary_checkout_currency || null,
    usd_checkout_enabled: !!store.usd_checkout_enabled,
    configured: !!(store.supported_checkout_currencies?.length && store.primary_checkout_currency),
  };
  const configured = !!cc.configured;
  const supported = cc.supported_checkout_currencies || [];
  const primary = cc.primary_checkout_currency;
  const usdOn = !!cc.usd_checkout_enabled;

  return (
    <div className="pf-card p-6 mt-6 max-w-2xl" data-testid="saas-currency-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-cyan-50 text-cyan-700 inline-flex items-center justify-center flex-shrink-0">
          <Coins className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Checkout currencies
          </div>
          {configured ? (
            <>
              <div
                className="mt-1 font-display text-xl font-medium tracking-tight text-slate-900 inline-flex flex-wrap items-center gap-2"
                data-testid="saas-currency-value"
              >
                {supported.map((code) => (
                  <span
                    key={code}
                    className={`inline-flex items-center gap-1 pf-chip ${
                      code === primary ? "bg-cyan-100 text-cyan-900" : "bg-slate-100 text-slate-700"
                    }`}
                    data-testid={`saas-currency-chip-${code.toLowerCase()}`}
                  >
                    {code === "USD" ? (
                      <CircleDollarSign className="w-3.5 h-3.5" />
                    ) : (
                      <DollarSign className="w-3.5 h-3.5" />
                    )}
                    {code}
                    {code === primary && (
                      <span className="text-[9px] uppercase tracking-widest">Primary</span>
                    )}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {usdOn
                  ? "Cyannect will accept USD transactions and calculate USD performance fees once Phase 2 is activated."
                  : "Cyannect will accept CAD transactions on your checkout."}
              </p>
            </>
          ) : (
            <>
              <div
                className="mt-1 font-display text-xl font-medium tracking-tight text-slate-900"
                data-testid="saas-currency-value"
              >
                Not configured yet
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Tell Cyannect which currencies your checkout accepts. Required before any
                USD-related billing goes live.
              </p>
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Not set yet — your SaaS conversion accounting can't reflect USD until you
                configure this.
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
              configured ? "border border-slate-200 text-slate-700 hover:bg-slate-50" : "pf-btn-primary"
            }`}
            data-testid={configured ? "saas-currency-update" : "saas-currency-set"}
          >
            {configured ? "Update Currencies" : "Configure Currencies"}
          </button>
        </div>
      </div>

      <SaasCheckoutCurrencyModal
        open={open}
        onClose={() => setOpen(false)}
        initial={{
          supported_checkout_currencies: supported,
          primary_checkout_currency: primary,
          usd_checkout_enabled: usdOn,
        }}
        onSaved={(nextStore) => onUpdated?.(nextStore)}
      />
    </div>
  );
}
